import {
  ButtonBase,
  CSSObject,
  Divider,
  SelectChangeEvent,
  Stack,
  styled,
  Theme,
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';

import {
  transactionFlowsSelector,
  useCreateTransactionMutation,
  useGetBeneficiariesQuery,
  useGetBeneficiaryVirtualAccountsQuery,
  useGetConfigurationQuery,
  useGetDashboardOperationsQuery,
} from '@/api/v1';
import { AmountInputWithFloatingCurrency } from '@/components/molecules/AmountInputWithFloatingCurrency';
import { CurrencyDropdown } from '@/components/molecules/CurrencyDropdown';
import { ExpectedSettlementTimeInfo } from '@/components/molecules/ExpectedSettlementTimeInfo';
import { FullScreenModalContainer } from '@/components/molecules/FullScreenModalContainer';
import {
  EmptyFundingMethodChip,
  FundingMethodChip,
} from '@/components/molecules/FundingMethodChip';
import { FxMarkupBanner } from '@/components/molecules/FxMarkupBanner';
import { FxRateLockSelector } from '@/components/molecules/FxRateLockSelector';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { OutsideBRLMarketHoursAlert } from '@/components/molecules/OutsideBRLMarketHoursAlert';
import { PaymentFlowModalMessage } from '@/components/molecules/PaymentFlowModalMessage';
import { Section } from '@/components/molecules/Section';
import { AmountAndCurrencyWidget } from '@/components/organisms/AmountAndCurrencyWidget';
import { ApplicationDialog } from '@/components/organisms/ApplicationDialog';
import { FiatAccountDetailsContent } from '@/components/organisms/FiatAccountDetailsContent';
import { formatPaymentInstructions } from '@/components/organisms/fiatAccountDetailsUtils';
import { FxMarkupModal } from '@/components/organisms/FxMarkupModal';
import { Header } from '@/components/organisms/Header';
import { TransactionDetailsAccordion } from '@/components/organisms/TransactionDetailsAccordion';
import { PaymentInstructionsResponse } from '@/DTOs/paymentInstruction';
import { VirtualAccount } from '@/DTOs/virtualAccount';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useCustomer } from '@/hooks/useCustomer';
import { useFxLockOptions } from '@/hooks/useFxLockOptions';
import { Simulation, useSimulation } from '@/hooks/useSimulation';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import {
  beneficiariesLoaded,
  beneficiaryChanged,
  DepositFlowAction,
  depositFlowFinished,
  depositFlowReducer,
  DepositFlowState,
  fromAmountChanged,
  fundingMethodChanged,
  initialState,
  mainBeneficiarySet,
  senderTypeChanged,
  simulationCreated,
  toAmountChanged,
  toCurrencyChanged,
  transactionFlowChanged,
} from '@/Screens/Dashboard/Flows/DepositFlow/reducer';
import { Beneficiary } from '@/types/beneficiary';
import {
  CurrencyCodeType,
  DashboardOperation,
  DashboardOperationDestination,
  PaymentRailType,
} from '@/types/currency';
import { isPaymentRailType } from '@/types/currency.ext';
import { SimulationResponse } from '@/types/simulation';
import { MarketExecutionType, TransactionRequest, TransactionResponse } from '@/types/transaction';
import { isBRLOperationOutsideMarketHours } from '@/utility/brlMarketHours';
import { toValidCurrency } from '@/utility/currencies';
import { isStablecoin } from '@/utility/isStablecoin';
import { AsAnyActionArg } from '@/utility/reducers';
import { toBeneficiary } from '@/utility/toBeneficiary';
import { transactionResponseToNavigationState } from '@/utility/transactionResponseToNavigationState';

enum SubmitError {
  REQUIRED_AMOUNT = 'REQUIRED_AMOUNT',
  REQUIRED_FUNDING_METHOD = 'REQUIRED_FUNDING_METHOD',
}

export const DepositFlow: React.FC = (): React.ReactElement => {
  const { data: flows = [], isLoading: isLoadingFlows } = useGetDashboardOperationsQuery('DEPOSIT');
  const { data: fetchedBeneficiariesPage, isLoading: isLoadingBeneficiaries } =
    useGetBeneficiariesQuery();
  const fetchedBeneficiaries = React.useMemo(
    (): readonly Beneficiary[] => (fetchedBeneficiariesPage?.content ?? []).map(toBeneficiary),
    [fetchedBeneficiariesPage],
  );
  const { data: selectorFlows = [] } = useSelector(transactionFlowsSelector('DEPOSIT'));
  const customer = useCustomer();
  const tr = useTranslator();
  const navigate = useNavigate();
  const toast = useToast();
  const currencyFormatter = useCurrencyFormatter();

  const reducer = React.useReducer<DepositFlowState, AsAnyActionArg<DepositFlowAction>>(
    depositFlowReducer,
    initialState,
  );

  const [state, dispatch] = reducer;
  const {
    beneficiaries,
    selectedBeneficiaryId,
    senderType,
    mainBeneficiaryId,
    fromAmount,
    fromCurrency,
    toAmount,
    toCurrency,
    fundingMethods,
    selectedFundingMethod,
    simulationId,
    reviewRevealed,
  } = state;

  const [error, setError] = React.useState<SubmitError | null>(null);
  const [isFxMarkupModalOpen, setIsFxMarkupModalOpen] = React.useState(false);
  const [appliedMarkupBps, setAppliedMarkupBps] = React.useState<number | null>(null);
  const inputContainerRef = React.useRef<HTMLDivElement>(null);

  const [createTransaction, createTransactionResult] = useCreateTransactionMutation();

  // --- Initialization effects ---

  React.useEffect(() => {
    if (flows.length > 0) {
      dispatch(transactionFlowChanged(flows[0]));

      return (): void => {
        dispatch(depositFlowFinished());
      };
    }
  }, [dispatch, flows]);

  React.useEffect((): void => {
    if (fetchedBeneficiaries.length > 0 && beneficiaries.length === 0 && customer.beneficiaryId) {
      dispatch(beneficiariesLoaded(fetchedBeneficiaries));
      dispatch(mainBeneficiarySet(customer.beneficiaryId));

      if (fetchedBeneficiaries.length === 1 && !selectedBeneficiaryId) {
        dispatch(beneficiaryChanged(customer.beneficiaryId));
      }
    }
  }, [
    fetchedBeneficiaries,
    beneficiaries.length,
    customer.beneficiaryId,
    selectedBeneficiaryId,
    dispatch,
  ]);

  // --- Derived values ---

  const effectiveBeneficiaryId = selectedBeneficiaryId || customer.beneficiaryId || '';

  const displayBeneficiaries = React.useMemo((): Beneficiary[] => {
    return beneficiaries.filter((b: Beneficiary): boolean => b.id !== mainBeneficiaryId);
  }, [beneficiaries, mainBeneficiaryId]);

  const hasSenderSelection = beneficiaries.length > 1;

  const senderSelected = React.useMemo((): boolean => {
    if (!hasSenderSelection) {
      return true;
    }
    if (senderType === 'myself') {
      return true;
    }
    if (senderType === 'customer' && !!selectedBeneficiaryId) {
      return true;
    }
    return false;
  }, [hasSenderSelection, senderType, selectedBeneficiaryId]);

  // --- Sender handlers ---

  const handleSenderTypeChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const newSenderType = event.target.value;
      if (newSenderType !== 'myself' && newSenderType !== 'customer') {
        return;
      }
      dispatch(senderTypeChanged(newSenderType));

      if (newSenderType === 'myself' && customer.beneficiaryId) {
        dispatch(beneficiaryChanged(customer.beneficiaryId));
      } else {
        dispatch(beneficiaryChanged(''));
      }
    },
    [dispatch, customer.beneficiaryId],
  );

  const handleBeneficiaryChange = React.useCallback(
    (event: SelectChangeEvent<string>): void => {
      dispatch(beneficiaryChanged(event.target.value));
    },
    [dispatch],
  );

  // --- Amount / funding method section ---

  const { data: virtualAccounts = [] } = useGetBeneficiaryVirtualAccountsQuery(
    effectiveBeneficiaryId,
    {
      skip: !effectiveBeneficiaryId,
    },
  );

  const paymentInstructions = React.useMemo((): PaymentInstructionsResponse | null => {
    const virtualAccount: VirtualAccount | undefined = virtualAccounts[0];
    if (!virtualAccount) {
      return null;
    }
    return {
      accountNumber: virtualAccount.accountNumber ?? '',
      routingNumber: virtualAccount.routingNumber ?? '',
      receiverName: virtualAccount.receiverName ?? '',
      receiverAddress: virtualAccount.receiverAddress,
      receiverBankName: virtualAccount.receiverBankName ?? '',
      receiverBankAddress: virtualAccount.receiverBankAddress ?? '',
      swiftCode: virtualAccount.swiftCode,
      reference: null,
    };
  }, [virtualAccounts]);

  const fromCurrencies = React.useMemo(
    (): readonly CurrencyCodeType[] =>
      selectorFlows
        .map((operation: DashboardOperation): CurrencyCodeType => operation.currency)
        .filter((currency: CurrencyCodeType): boolean => {
          if (currency === CurrencyCodeType.USD && virtualAccounts.length === 0) {
            return false;
          }
          return true;
        }),
    [selectorFlows, virtualAccounts],
  );

  const information = React.useMemo((): React.ReactElement | null => {
    if (
      fromCurrency === CurrencyCodeType.UNSET ||
      fromCurrency === CurrencyCodeType.USD ||
      isStablecoin(fromCurrency)
    ) {
      return null;
    }

    return (
      <Typography component="span" key="text" className="inherit">
        {tr('CURRENCY_CONVERT_TO_STABLECOIN', fromCurrency)}{' '}
      </Typography>
    );
  }, [fromCurrency, tr]);

  const message = React.useMemo(
    (): ReadonlyArray<string | React.ReactElement> => (information ? [information] : []),
    [information],
  );

  const displayFundingMethodValue = React.useCallback(
    (value: unknown): React.ReactNode => {
      if (typeof value === 'string' && value && isPaymentRailType(value)) {
        return <FundingMethodChip fundingMethod={value} />;
      }
      return <EmptyFundingMethodChip label={tr('FUNDING_METHOD')} />;
    },
    [tr],
  );

  const focusInput = React.useCallback((): void => {
    const { current: container } = inputContainerRef;
    if (!container) {
      return;
    }

    setTimeout((): void => {
      const input = container.querySelector('input');
      input?.focus();
    }, 0);
  }, []);

  const updateSelectedCurrency = React.useCallback(
    (value: string): void => {
      const flow = selectorFlows.find(
        (operation: DashboardOperation): boolean => operation.currency === value,
      );
      if (!flow) {
        throw new Error(`no flow found for currency: ${value}`);
      }

      dispatch(transactionFlowChanged(flow));
      dispatch(fromAmountChanged(null));

      focusInput();
    },
    [dispatch, selectorFlows, focusInput],
  );

  React.useEffect((): void => {
    if (fromCurrencies.length > 0 && !fromCurrencies.includes(fromCurrency)) {
      updateSelectedCurrency(fromCurrencies[0]);
    }
  }, [fromCurrencies, fromCurrency, updateSelectedCurrency]);

  const onCurrencyChange = React.useCallback(
    (value: string): void => {
      updateSelectedCurrency(value);
    },
    [updateSelectedCurrency],
  );

  const handleAmountChange = React.useCallback(
    (amount: number | null): void => {
      if (error && error === SubmitError.REQUIRED_AMOUNT) {
        setError(null);
      }

      dispatch(fromAmountChanged(amount));
    },
    [error, setError, dispatch],
  );

  const onFundingMethodChange = React.useCallback(
    (event: SelectChangeEvent<unknown>, _: React.ReactNode): void => {
      if (error && error === SubmitError.REQUIRED_FUNDING_METHOD) {
        setError(null);
      }

      const { value } = event.target;
      if (isPaymentRailType(value)) {
        dispatch(fundingMethodChanged(value));
      }
    },
    [error, setError, dispatch],
  );

  const onCopyAll = React.useCallback((): void => {
    if (!paymentInstructions) {
      return;
    }

    const text = formatPaymentInstructions({
      instructions: paymentInstructions,
      paymentRail: selectedFundingMethod,
      tr: tr as (key: string, ...args: unknown[]) => string,
      fromAmount: fromAmount,
      fromCurrency: fromCurrency,
      toAmount: fromAmount,
      toCurrency: CurrencyCodeType.USD,
      formatAmount: currencyFormatter.format,
    });
    navigator.clipboard.writeText(text).then(
      (): void => {
        toast.show(tr('ACCOUNT_DETAILS_COPIED'), 'success');
      },
      (): void => {
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
      },
    );
  }, [
    paymentInstructions,
    selectedFundingMethod,
    tr,
    toast,
    fromAmount,
    fromCurrency,
    currencyFormatter,
  ]);

  const {
    data: configuration,
    isFetching: isFetchingConfiguration,
    isError: isErrorConfiguration,
  } = useGetConfigurationQuery();

  const shouldNotAllowOperation = React.useMemo((): boolean => {
    if (isErrorConfiguration || !configuration?.brazilianMarketHours) {
      return false;
    }
    return (
      isFetchingConfiguration ||
      isBRLOperationOutsideMarketHours(fromCurrency, configuration.brazilianMarketHours)
    );
  }, [
    isErrorConfiguration,
    configuration?.brazilianMarketHours,
    isFetchingConfiguration,
    fromCurrency,
  ]);

  const isFiatAccountFlow =
    (selectedFundingMethod === PaymentRailType.ACH ||
      selectedFundingMethod === PaymentRailType.Wire ||
      selectedFundingMethod === PaymentRailType.RTP ||
      selectedFundingMethod === PaymentRailType.Swift) &&
    !!paymentInstructions;

  const showFxMarkup =
    senderType === 'customer' && toValidCurrency(fromCurrency) !== toValidCurrency(toCurrency);

  React.useEffect((): void => {
    if (toValidCurrency(fromCurrency) === toValidCurrency(toCurrency)) {
      setAppliedMarkupBps(null);
    }
  }, [fromCurrency, toCurrency]);

  // --- Review section (simulation) ---

  const [simulation, simulating] = useSimulation({
    simulationId: simulationId,
    fromAmount: fromAmount,
    toAmount: toAmount,
    fromCurrency: fromCurrency,
    toCurrency: toCurrency,
    fundingMethod: selectedFundingMethod,
    beneficiaryId: selectedBeneficiaryId || undefined,
  });

  React.useEffect((): void => {
    if (simulation.id === '') {
      return;
    }

    dispatch(simulationCreated(simulation));
  }, [dispatch, simulation]);

  const isCrossCurrency = toValidCurrency(fromCurrency) !== toValidCurrency(toCurrency);

  const requiresDeposit =
    selectedFundingMethod !== null && selectedFundingMethod !== PaymentRailType.CalizaWallet;

  const fxLockOptions = useFxLockOptions({
    parentSimulationId: simulation.id,
    enabled: isCrossCurrency && requiresDeposit,
    markupBps: appliedMarkupBps,
  });

  const displaySimulation = React.useMemo((): Simulation => {
    if (fxLockOptions.effectiveSimulation) {
      return {
        id: fxLockOptions.effectiveSimulation.id,
        transactionDetails: fxLockOptions.effectiveSimulation.transactionDetails,
        to: fxLockOptions.effectiveSimulation.to,
        from: fxLockOptions.effectiveSimulation.from,
        expiresAt: fxLockOptions.effectiveSimulation.expiresAt,
      };
    }
    return simulation;
  }, [simulation, fxLockOptions.effectiveSimulation]);

  const toCurrencies = React.useMemo(
    (): readonly CurrencyCodeType[] =>
      selectorFlows
        .find((operation: DashboardOperation): boolean => fromCurrency === operation.currency)
        ?.destinations?.filter((destination: DashboardOperationDestination): boolean =>
          destination.fundingMethods.includes(selectedFundingMethod),
        )
        .map(
          (destination: DashboardOperationDestination): CurrencyCodeType => destination.currency,
        ) ?? [],
    [selectorFlows, fromCurrency, selectedFundingMethod],
  );

  const resetAmountRequiredError = React.useCallback((): void => {
    setError((error: SubmitError | null): SubmitError | null => {
      if (error && error === SubmitError.REQUIRED_AMOUNT) {
        return null;
      }
      return error;
    });
  }, []);

  const onToAmountChange = React.useCallback(
    (value: number | null): void => {
      dispatch(toAmountChanged(value));
      resetAmountRequiredError();
    },
    [resetAmountRequiredError, dispatch],
  );

  const onFromAmountChange = React.useCallback(
    (value: number | null): void => {
      dispatch(fromAmountChanged(value));
      resetAmountRequiredError();
    },
    [resetAmountRequiredError, dispatch],
  );

  const onToCurrencyChange = React.useCallback(
    (currency: CurrencyCodeType): void => {
      dispatch(toCurrencyChanged(currency));
    },
    [dispatch],
  );

  // --- Confirm ---

  const onConfirm = React.useCallback((): void => {
    if (!fromAmount || !toAmount) {
      setError(SubmitError.REQUIRED_AMOUNT);
      return;
    }

    if (!simulation.id || simulation.id === '') {
      console.debug('no simulation id');
      return;
    }

    const transactionRequest: TransactionRequest = {
      simulationId: fxLockOptions.effectiveSimulationId ?? simulation.id!,
      beneficiaryIp: '0.0.0.0',
      marketExecutionType: MarketExecutionType.executeImmediately,
    };
    createTransaction(transactionRequest);
  }, [fromAmount, toAmount, simulation.id, fxLockOptions.effectiveSimulationId, createTransaction]);

  const tryToNavigateToPayment = React.useCallback(
    (response: TransactionResponse): void => {
      const search = new URLSearchParams();

      search.set('transactionId', response.id);
      navigate(
        {
          pathname: '/home/deposit/payment',
          search: search.toString(),
        },
        {
          state: transactionResponseToNavigationState(response, selectedFundingMethod),
        },
      );
    },
    [selectedFundingMethod, navigate],
  );

  // Track which transaction we've already navigated for to prevent redirect loops
  const navigatedTransactionIdRef = React.useRef<string | null>(null);

  React.useEffect((): void => {
    switch (createTransactionResult.status) {
      case QueryStatus.fulfilled: {
        const transactionId = createTransactionResult.data.id;
        // Only navigate if we haven't already navigated for this transaction
        if (navigatedTransactionIdRef.current !== transactionId) {
          navigatedTransactionIdRef.current = transactionId;
          tryToNavigateToPayment(createTransactionResult.data);
        }
        break;
      }
      case QueryStatus.rejected:
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        break;
      default:
        break;
    }
  }, [
    createTransactionResult.data,
    createTransactionResult.status,
    tryToNavigateToPayment,
    toast,
    tr,
  ]);

  // --- Visibility conditions ---

  const showDepositDetails = senderSelected && !isLoadingBeneficiaries;
  const showReview = senderSelected && !!selectedFundingMethod && reviewRevealed;
  const showFiatAccountCredentials = isFiatAccountFlow && !!simulation.id;

  const creatingTransaction = createTransactionResult.isLoading;

  const confirmDisabled = React.useMemo((): boolean => {
    if (!simulation?.id) {
      return true;
    }

    return (
      shouldNotAllowOperation ||
      fxLockOptions.isLoading ||
      createTransactionResult.status === QueryStatus.pending ||
      createTransactionResult.status === QueryStatus.fulfilled
    );
  }, [
    createTransactionResult.status,
    shouldNotAllowOperation,
    simulation?.id,
    fxLockOptions.isLoading,
  ]);

  const onClose = React.useCallback((): void => {
    navigate('/home', { replace: true });
  }, [navigate]);

  return (
    <ApplicationDialog fullScreen={true}>
      <Header title="DEPOSIT" onClose={onClose} />
      <FullScreenModalContainer>
        {/* Section 1 — Sender (only when multiple beneficiaries) */}
        {hasSenderSelection && (
          <Section
            title={tr('WHO_ARE_YOU_DEPOSITING_FOR')}
            visible={true}
            working={creatingTransaction}>
            <FormControl fullWidth>
              <RadioGroup value={senderType || ''} onChange={handleSenderTypeChange}>
                <StyledFormControlLabel value="myself" control={<Radio />} label={tr('MYSELF')} />
                <StyledFormControlLabel
                  value="customer"
                  control={<Radio />}
                  label={tr('CUSTOMER')}
                />
              </RadioGroup>
            </FormControl>
            {senderType === 'customer' && (
              <Box mt={3}>
                <FormControl fullWidth>
                  <InputLabel id="deposit-beneficiary-select-label">
                    {tr('SELECT_CUSTOMER')}
                  </InputLabel>
                  <Select
                    labelId="deposit-beneficiary-select-label"
                    id="deposit-beneficiary-select"
                    value={selectedBeneficiaryId}
                    label={tr('SELECT_CUSTOMER')}
                    onChange={handleBeneficiaryChange}>
                    {displayBeneficiaries.map(
                      (beneficiary: Beneficiary): React.ReactElement => (
                        <MenuItem key={beneficiary.id} value={beneficiary.id}>
                          {beneficiary.name}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Section>
        )}

        {/* Section 2 — Deposit Details */}
        <Section
          title={tr('CHOOSE_DEPOSIT_AMOUNT')}
          visible={showDepositDetails}
          working={creatingTransaction || isLoadingFlows}>
          {fromCurrencies.length === 0 && !isLoadingFlows ? (
            <Typography variant="body2" color="custom.LightGray">
              {tr('NO_DEPOSIT_OPTIONS_AVAILABLE')}
            </Typography>
          ) : (
            <>
              <Box display="flex" alignItems="center" gap={1} height={34}>
                <CurrencyDropdown
                  value={fromCurrency}
                  currencies={fromCurrencies}
                  onChange={onCurrencyChange}
                />
                {fromCurrency !== CurrencyCodeType.UNSET
                  ? ((): React.ReactNode => {
                      if (fundingMethods.length <= 1) {
                        return (
                          <FundingMethodItem display="flex" alignItems="center">
                            <FundingMethodChip fundingMethod={selectedFundingMethod} />
                          </FundingMethodItem>
                        );
                      } else {
                        return (
                          <FundingMethodSelectStyled
                            displayEmpty={true}
                            renderValue={displayFundingMethodValue}
                            value={selectedFundingMethod}
                            error={error === SubmitError.REQUIRED_FUNDING_METHOD}
                            onChange={onFundingMethodChange}>
                            {fundingMethods.map(
                              (fundingMethod: PaymentRailType): React.ReactElement => (
                                <FundingMethodMenuItem key={fundingMethod} value={fundingMethod}>
                                  <FundingMethodChip fundingMethod={fundingMethod} />
                                </FundingMethodMenuItem>
                              ),
                            )}
                          </FundingMethodSelectStyled>
                        );
                      }
                    })()
                  : null}
              </Box>

              <Box ref={inputContainerRef}>
                <AmountInputWithFloatingCurrency
                  currency={fromCurrency}
                  value={fromAmount}
                  onChange={handleAmountChange}
                />
              </Box>
              {shouldNotAllowOperation ? (
                <OutsideBRLMarketHoursAlert />
              ) : !isFiatAccountFlow ? (
                <PaymentFlowModalMessage type="info" message={message} />
              ) : null}
              {error ? (
                <Box mt={2} display="flex" justifyContent="center">
                  <Typography variant="body2" color="error">
                    {tr(error)}
                  </Typography>
                </Box>
              ) : null}
            </>
          )}
        </Section>

        {/* Section 3 — Review */}
        <Section
          title={tr('REVIEW_CONVERSION')}
          visible={showReview}
          spotlight={true}
          working={simulating || creatingTransaction}>
          <Stack gap={2}>
            <AmountAndCurrencyWidget
              label={tr('YOU_SEND')}
              size="normal"
              amount={fromAmount}
              currency={fromCurrency}
              currencies={fromCurrencies}
              error={error === SubmitError.REQUIRED_AMOUNT}
              currencyDisabled={true}
              disabled={simulating}
              placeholder={simulating && fromAmount === null}
              onAmountChange={onFromAmountChange}
            />
            <AmountAndCurrencyWidget
              label={tr('AMOUNT_ADDED_TO_WALLET')}
              size="normal"
              amount={toAmount}
              currency={toCurrency}
              currencies={toCurrencies}
              disabled={simulating}
              placeholder={simulating && toAmount === null}
              onAmountChange={onToAmountChange}
              onCurrencyChange={onToCurrencyChange}
            />

            {isCrossCurrency && requiresDeposit && (
              <FxRateLockSelector
                options={fxLockOptions.fxLockOptions}
                selectedFxLockType={fxLockOptions.selectedFxLockType}
                onSelect={fxLockOptions.selectFxLockType}
                isLoading={fxLockOptions.isLoading}
                disabled={fromAmount === null && toAmount === null}
                fromCurrency={fromCurrency}
                toCurrency={toCurrency}
              />
            )}

            {showFxMarkup && (
              <FxMarkupBanner
                currentMarkupBps={appliedMarkupBps}
                disabled={fromAmount === null && toAmount === null}
                onAdd={(): void => setIsFxMarkupModalOpen(true)}
                onRemove={(): void => setAppliedMarkupBps(null)}
              />
            )}

            <TransactionDetailsAccordion
              simulating={simulating}
              simulation={displaySimulation}
              fromAmount={displaySimulation.from.value}
              fromCurrency={fromCurrency}
              fxLockSpreadPercentage={fxLockOptions.effectiveSimulation?.fxLockSpreadPercentage}
            />
          </Stack>

          <Box sx={styles.expectedSettlementTimeInfo}>
            <ExpectedSettlementTimeInfo
              expectedSettlementTimeDetails={
                displaySimulation.transactionDetails.expectedSettlementTimeDetails
              }
            />
          </Box>
        </Section>

        {showFxMarkup && (
          <FxMarkupModal
            open={isFxMarkupModalOpen}
            onClose={(): void => setIsFxMarkupModalOpen(false)}
            onApply={(bps: number, previewSimulation: SimulationResponse | null): void => {
              if (previewSimulation) {
                fxLockOptions.setRecreatedSimulation(previewSimulation);
              }
              setAppliedMarkupBps(bps);
              setIsFxMarkupModalOpen(false);
            }}
            currentMarkupBps={appliedMarkupBps}
            simulation={displaySimulation}
            parentSimulationId={simulation.id}
            selectedLockSimulationId={fxLockOptions.effectiveSimulation?.id ?? null}
            fromCurrency={fromCurrency}
            toCurrency={toCurrency}
            isLoading={simulating || fxLockOptions.isLoading}
            fxLockSpreadPercentage={fxLockOptions.effectiveSimulation?.fxLockSpreadPercentage}
          />
        )}

        {/* Section 4 — USD Credentials (only for fiat account flow after simulation) */}
        {showFiatAccountCredentials && paymentInstructions && (
          <Section title="" visible={true}>
            <Box sx={styles.credentialsContainer}>
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="h7">{tr('DEPOSIT_CREDENTIALS_TITLE')}</Typography>
                <Typography variant="body2" color="custom.LightGray" whiteSpace="pre-line">
                  {tr('DEPOSIT_CREDENTIALS_DESCRIPTION')}
                </Typography>
              </Box>
              <Box my={4}>
                <Divider color="custom.DividerMed" />
              </Box>
              <FiatAccountDetailsContent
                paymentInstructions={paymentInstructions}
                paymentRail={selectedFundingMethod}
                fromAmount={fromAmount}
                fromCurrency={fromCurrency}
                toAmount={simulation.to.value ?? fromAmount}
                toCurrency={CurrencyCodeType.USD}
              />
              <Box sx={styles.copyAllButton}>
                <Button variant="dark" size="small" onClick={onCopyAll}>
                  {tr('COPY_ALL')}
                </Button>
              </Box>
            </Box>
          </Section>
        )}

        {/* Action Buttons */}
        {showReview && (
          <Box width={552} mx="auto">
            {isFiatAccountFlow ? (
              <ButtonBase role="button" sx={[styles.closeButton]} onClick={onClose}>
                <Typography
                  fontSize={16}
                  fontWeight={400}
                  lineHeight="24px"
                  color="primary.contrastText">
                  {tr('CLOSE')}
                </Typography>
              </ButtonBase>
            ) : (
              <LoadingButton
                variant="dark"
                fullWidth={true}
                loading={creatingTransaction}
                disabled={confirmDisabled}
                onClick={onConfirm}>
                {tr('CONTINUE')}
              </LoadingButton>
            )}
            {error ? (
              <Box mt={2} display="flex" justifyContent="center">
                <Typography variant="body2" color="error">
                  {tr(error)}
                </Typography>
              </Box>
            ) : null}
          </Box>
        )}
      </FullScreenModalContainer>

      <Outlet context={reducer} />
    </ApplicationDialog>
  );
};

const StyledFormControlLabel = styled(FormControlLabel)(
  ({ theme }: { theme: Theme }): CSSObject => ({
    alignItems: 'center',
    color: theme.palette.text.primary,
  }),
);

const FundingMethodMenuItem = styled(MenuItem)({
  height: 40,
  paddingLeft: 8,
  paddingRight: 16,
});

const FundingMethodSelectStyled = styled(Select)(
  ({ theme }: { theme: Theme }): CSSObject => ({
    backgroundColor: theme.palette.background.paper,
    height: 32,
    '& .MuiInputBase-input.MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      paddingLeft: 8,
      paddingRight: 35,
      paddingTop: 0,
      paddingBottom: 0,
      width: '100%',
      height: '100%',
    },
    '& .MuiSelect-icon': {
      cursor: 'pointer',
      right: 8,
    },
  }),
);

const FundingMethodItem = styled(Box)(
  ({ theme }: { theme: Theme }): CSSObject => ({
    backgroundColor: theme.palette.custom.InputsBackground,
    height: 32,
    border: `1px solid ${theme.palette.custom.DividerLight}`,
    borderRadius: 24,
    paddingLeft: '8px!important',
    paddingRight: '8px!important',
  }),
);

const styles: Record<string, SystemStyleObject> = {
  closeButton: {
    width: '100%',
    height: 56,
    borderRadius: 10,
    backgroundColor: 'custom.BlackLightBackground',
  },
  copyAllButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    mt: 2,
  },
  credentialsContainer: {
    backgroundColor: 'custom.InputsBackground',
    borderRadius: 4,
    px: 5,
    py: 4,
  },
  expectedSettlementTimeInfo: {
    display: 'flex',
    justifyContent: 'center',
    mt: 3,
    color: 'primary.main',
  },
};
